import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const SEGMENTS = [
  { v: "all", label: "Everyone", sub: "Non-banned users" },
  { v: "creators", label: "Creators", sub: "is_creator = true" },
  { v: "ambassadors", label: "Ambassadors", sub: "Approved referral crew" },
  { v: "desire", label: "By desire", sub: "Pick a tag" },
];

/**
 * Segment tile picker + desire-tag input + live count preview. Uses the
 * backend /admin/broadcast/segment-count endpoint (fetched by parent) so
 * this stays purely presentational.
 */
export default function BroadcastSegmentPicker({
  segment,
  onSegmentChange,
  desireTag,
  onDesireTagChange,
  countLoading,
  segmentCount,
}) {
  return (
    <div className="border-t border-white/5 pt-3">
      <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
        Audience
      </label>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="broadcast-segments">
        {SEGMENTS.map(({ v, label, sub }) => {
          const active = segment === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onSegmentChange(v)}
              data-testid={`broadcast-segment-${v}`}
              className={`text-left px-3 py-2.5 border transition-colors ${
                active
                  ? "bg-rose-600/20 border-rose-500 text-white"
                  : "bg-black/30 border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
              }`}
            >
              <div className="text-sm font-medium">{label}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{sub}</div>
            </button>
          );
        })}
      </div>
      {segment === "desire" && (
        <Input
          value={desireTag}
          onChange={(e) => onDesireTagChange(e.target.value)}
          placeholder="e.g. Romantic, Kinky, Playful…"
          className="mt-2 bg-black/40 border-rose-500/40 rounded-none py-5"
          data-testid="broadcast-desire-tag"
        />
      )}
      <CountPreview
        segment={segment}
        desireTag={desireTag}
        loading={countLoading}
        count={segmentCount}
      />
    </div>
  );
}

/** Sub-piece: renders the "This will reach N members" line under the picker. */
function CountPreview({ segment, desireTag, loading, count }) {
  if (loading) {
    return (
      <div className="mt-2 text-xs" data-testid="broadcast-audience-count">
        <span className="text-zinc-500 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Counting audience…
        </span>
      </div>
    );
  }
  if (count !== null && count !== undefined) {
    const tone = count === 0 ? "text-amber-400" : "text-emerald-400";
    const msg = count === 0
      ? "No members match this segment — pick another."
      : `This will reach ${count.toLocaleString()} ${count === 1 ? "member" : "members"}.`;
    return (
      <div className="mt-2 text-xs" data-testid="broadcast-audience-count">
        <span className={tone}>{msg}</span>
      </div>
    );
  }
  if (segment === "desire" && !desireTag.trim()) {
    return (
      <div className="mt-2 text-xs" data-testid="broadcast-audience-count">
        <span className="text-zinc-500">Type a desire tag to see the count.</span>
      </div>
    );
  }
  return null;
}
